'use client';

import React, { useMemo } from 'react';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetProviderVolumeQuery } from 'src/api/redux';

import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  applyDisplayMode,
  claimsOrEncounters,
} from 'src/components/Profiles/shared/widgets/utils';
import MonthlyAvgBadge from 'src/components/Profiles/shared/widgets/MonthlyAvgBadge';

export interface CodeSetVolumeRatioItem {
  id: string;
  /** Ratio of source to comparison (sourceVolume / comparisonVolume), or null if comparison is 0 */
  ratio: number | null;
  sourceLabel: string;
  sourceVolume: number;
  sourcePct: number;
  comparisonLabel: string;
  comparisonVolume: number;
  comparisonPct: number;
  totalVolume: number;
  volumeLabel: string;
}

const RatioSection: React.FC<{
  item: CodeSetVolumeRatioItem;
  dateRangeLabel: string;
}> = ({ item, dateRangeLabel }) => {
  const ratioDisplay =
    item.ratio !== null ? item.ratio.toFixed(1) : '\u2014';

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="code-set-volume-ratio-section"
    >
      {/* Hero ratio */}
      <div className="flex items-baseline gap-1">
        <span
          className="text-4xl font-bold text-sky-900 tabular-nums"
          data-testid="ratio-hero-value"
        >
          {ratioDisplay}
        </span>
        {item.ratio !== null && (
          <span className="text-4xl font-bold text-neutral-400 tabular-nums">
            : 1
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-neutral-600" data-testid="ratio-description">
        <span className="font-bold">{item.sourceLabel}</span>
        {' to '}
        <span className="font-bold">{item.comparisonLabel}</span>
      </p>

      {/* Stacked bar */}
      <div
        className="flex w-full h-10 gap-1"
        role="img"
        aria-label={`${item.sourceLabel}: ${item.sourceVolume.toLocaleString()} (${item.sourcePct}%), ${item.comparisonLabel}: ${item.comparisonVolume.toLocaleString()} (${item.comparisonPct}%)`}
        data-testid="ratio-bar"
      >
        {item.sourcePct > 0 && (
          <div
            className={`flex items-center justify-center bg-sky-600 text-white text-xs font-semibold tabular-nums ${item.comparisonPct > 0 ? 'rounded-l-lg' : 'rounded-lg'}`}
            data-testid="ratio-bar-source"
            style={{ width: `${item.sourcePct}%` }}
          >
            {item.sourceVolume.toLocaleString()} ({item.sourcePct}%)
          </div>
        )}
        {item.comparisonPct > 0 && (
          <div
            className={`flex items-center justify-center bg-neutral-400 text-white text-xs font-semibold tabular-nums ${item.sourcePct > 0 ? 'rounded-r-lg' : 'rounded-lg'}`}
            data-testid="ratio-bar-comparison"
            style={{ width: `${item.comparisonPct}%` }}
          >
            {item.comparisonVolume.toLocaleString()} ({item.comparisonPct}%)
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4" data-testid="ratio-legend">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-sky-600" />
          <span className="text-xs text-neutral-600">{item.sourceLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-neutral-400" />
          <span className="text-xs text-neutral-600">
            {item.comparisonLabel}
          </span>
        </div>
      </div>

      {/* Footer */}
      <p className="text-xs text-neutral-400" data-testid="ratio-footer">
        Source: {dateRangeLabel} data &middot; Total:{' '}
        {item.totalVolume.toLocaleString()} {item.volumeLabel}
      </p>
    </div>
  );
};

const CodeSetVolumeGap: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const dateRangeLabel = widgetConfig?.dateRangeLabel ?? '';
  const { entityDetails } = useProfile();

  const entityId = entityDetails?.id;
  const { showEncountersTotals } = useFlags();
  const displayMode = widget?.display_mode;
  const isMonthlyAvg = displayMode === 'monthly_average';

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  // Each slot can be a code set OR an encounter (mutually exclusive)
  const sourceCodeSetId = widget?.code_set?.id;
  const sourceEncounterId = widget?.encounter?.id;
  const comparisonCodeSetId = widget?.comparison_code_set?.id;
  const comparisonEncounterId = widget?.comparison_encounter?.id;

  const hasSourceId = !!(sourceCodeSetId || sourceEncounterId);
  const hasComparisonId = !!(comparisonCodeSetId || comparisonEncounterId);

  const { data: sourceData, isLoading: sourceLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: sourceCodeSetId ?? undefined,
        cohort_id: sourceEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasSourceId }
    );

  const { data: comparisonData, isLoading: comparisonLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: comparisonCodeSetId ?? undefined,
        cohort_id: comparisonEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasComparisonId }
    );

  const isLoading = sourceLoading || comparisonLoading;

  const sourceLabel =
    widget?.code_set?.label ?? widget?.encounter?.label ?? 'Source';
  const comparisonLabel =
    widget?.comparison_code_set?.label ??
    widget?.comparison_encounter?.label ??
    'Comparison';

  const volumeType = widget?.volume_type ?? 'claims';

  const ratioItem = useMemo<CodeSetVolumeRatioItem | null>(() => {
    if (!sourceData || !comparisonData) return null;

    // For a cohort (encounter) slot, the claims role reads encounters when flagged.
    const sourceRaw =
      volumeType === 'patients'
        ? Number(sourceData.patients ?? 0)
        : Number(
            claimsOrEncounters(
              sourceData,
              !!sourceEncounterId,
              showEncountersTotals
            ) ?? 0
          );
    const comparisonRaw =
      volumeType === 'patients'
        ? Number(comparisonData.patients ?? 0)
        : Number(
            claimsOrEncounters(
              comparisonData,
              !!comparisonEncounterId,
              showEncountersTotals
            ) ?? 0
          );
    const sourceVolume = applyDisplayMode(sourceRaw, displayMode);
    const comparisonVolume = applyDisplayMode(comparisonRaw, displayMode);
    const totalVolume = sourceVolume + comparisonVolume;

    const ratio =
      comparisonVolume > 0
        ? Math.round((sourceVolume / comparisonVolume) * 10) / 10
        : null;

    const sourcePct =
      totalVolume > 0
        ? Math.round((sourceVolume / totalVolume) * 1000) / 10
        : 0;
    const comparisonPct =
      totalVolume > 0
        ? Math.round((comparisonVolume / totalVolume) * 1000) / 10
        : 0;

    return {
      id: `${sourceCodeSetId ?? sourceEncounterId}-${comparisonCodeSetId ?? comparisonEncounterId}`,
      ratio,
      sourceLabel,
      sourceVolume,
      sourcePct,
      comparisonLabel,
      comparisonVolume,
      comparisonPct,
      totalVolume,
      volumeLabel: volumeType,
    };
  }, [
    sourceData,
    comparisonData,
    sourceCodeSetId,
    sourceEncounterId,
    comparisonCodeSetId,
    comparisonEncounterId,
    sourceLabel,
    comparisonLabel,
    displayMode,
    volumeType,
    showEncountersTotals,
  ]);

  const hasData = !!ratioItem;

  return (
    <Card
      className="p-5 flex flex-col gap-3 bg-white h-full"
      data-testid="code-set-volume-ratio"
      role="region"
      aria-label={widget?.label ?? 'Code Set Volume Ratio'}
    >
      <WidgetHeader
        fallbackTitle="Ratio Analysis"
        trailing={isMonthlyAvg ? <MonthlyAvgBadge /> : undefined}
      />
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <WidgetLoading data-testid="code-set-volume-ratio-loading" className="flex items-center justify-center py-8" />
        ) : !hasData || !ratioItem ? (
          <WidgetEmpty data-testid="code-set-volume-ratio-empty" variant="full" message="No ratio data available" />
        ) : (
          <RatioSection item={ratioItem} dateRangeLabel={dateRangeLabel} />
        )}
      </div>
    </Card>
  );
};

export default CodeSetVolumeGap;
